import { ReactNode } from 'react';
import { useCurrentAssistant } from '@/hooks/useAssistantAuth';
import type { AssistantPermissions } from '@/types/entities';

interface WithPermissionProps {
  children: ReactNode;
  page: keyof AssistantPermissions;
  action: string;
  fallback?: ReactNode;
}

/**
 * Component to conditionally render UI elements based on assistant permissions
 *
 * @example
 * <WithPermission page="menu" action="add">
 *   <Button>Add Menu Item</Button>
 * </WithPermission>
 *
 * @example With fallback
 * <WithPermission
 *   page="menu"
 *   action="delete"
 *   fallback={<span className="text-muted-foreground">No permission</span>}
 * >
 *   <Button variant="destructive">Delete</Button>
 * </WithPermission>
 */
export function WithPermission({ children, page, action, fallback = null }: WithPermissionProps) {
  const { data: assistant } = useCurrentAssistant();

  // Permission helper function
  const hasPermission = (p: keyof AssistantPermissions, a: string): boolean => {
    if (!assistant?.permissions || !assistant.permissions[p]) return false;
    const pagePermissions = assistant.permissions[p] as Record<string, boolean>;
    return pagePermissions[a] === true;
  };

  // If not logged in as assistant, don't render anything
  // (this component is specifically for assistants)
  if (!assistant) {
    return null;
  }

  // Check if assistant has required permission
  const hasRequiredPermission = hasPermission(page, action);

  if (!hasRequiredPermission) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
